import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Typography, Table, Tag, Select, Space, Tooltip, message,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { formatTS } from '../utils/time';
import api from '../api';

const SESSION_STATUS_OPTIONS = [
  { label: 'All',       value: '' },
  { label: 'Active',    value: 'active' },
  { label: 'Completed', value: 'completed' },
];

const PART_STATUS_OPTIONS = [
  { label: 'Any',       value: '' },
  { label: 'Pending',   value: 'pending' },
  { label: 'Completed', value: 'completed' },
];

const SESSION_STATUS_COLOR = {
  draft: 'default', active: 'blue', completed: 'green', canceled: 'red',
};
const SESSION_STATUS_LABEL = {
  draft: 'Draft', active: 'Active', completed: 'Completed', canceled: 'Canceled',
};
const PART_STATUS_COLOR = { pending: 'orange', completed: 'green', canceled: 'red' };
const PART_STATUS_LABEL = { pending: 'Pending', completed: 'Completed', canceled: 'Canceled' };

let _cache = null;

export default function MyCoachingSessionsPage() {
  const navigate = useNavigate();

  const restoredRef = useRef(!!_cache);
  const [sessions, setSessions]           = useState(() => _cache?.sessions      ?? []);
  const [loading, setLoading]             = useState(false);
  const [sessionStatus, setSessionStatus] = useState(() => _cache?.sessionStatus ?? '');
  const [partStatus,    setPartStatus]    = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { view: 'participant' };
      if (sessionStatus) params.status = sessionStatus;
      const res = await api.get('/api/coaching/sessions', { params });
      setSessions(res.data);
      _cache = { sessions: res.data, sessionStatus };
    } catch { message.error('Failed to load sessions.'); }
    finally { setLoading(false); }
  }, [sessionStatus]);

  useEffect(() => {
    if (restoredRef.current) { restoredRef.current = false; return; }
    load();
  }, [load]);

  // Client-side filter on the current user's own participation status.
  // Someone canceled off a session has nothing left to do on it, so those rows
  // are dropped rather than offered as a filter -- the API already withholds
  // canceled and draft sessions themselves.
  const mine = sessions.filter((s) => s.MyStatus !== 'canceled');
  const filtered = partStatus
    ? mine.filter((s) => s.MyStatus === partStatus)
    : mine;

  const columns = [
    {
      title: 'My Status', dataIndex: 'MyStatus', key: 'MyStatus', width: 120,
      render: (v) => v
        ? <Tag color={PART_STATUS_COLOR[v]}>{PART_STATUS_LABEL[v] ?? v}</Tag>
        : <Tag color="default">—</Tag>,
    },
    {
      title: 'Session', dataIndex: 'Title', key: 'Title',
      render: (v, r) => (
        <span
          style={{ color: '#1677ff', cursor: 'pointer' }}
          onClick={() => navigate(`/coaching/sessions/${r.ID}`)}
        >
          {v}
        </span>
      ),
    },
    {
      title: 'Session Status', dataIndex: 'Status', key: 'Status', width: 130,
      render: (v) => <Tag color={SESSION_STATUS_COLOR[v]}>{SESSION_STATUS_LABEL[v] ?? v}</Tag>,
    },
    { title: 'Campaign', dataIndex: 'Campaign', key: 'Campaign', width: 120 },
    { title: 'Request Type', dataIndex: 'RequestTypeName', key: 'RequestTypeName', width: 150,
      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
    { title: 'Area',  dataIndex: 'AreaName',  key: 'AreaName',  width: 150 },
    { title: 'Coach', dataIndex: 'CoachName', key: 'CoachName', width: 160 },
    {
      // One date, not two. A participant can be given their own due date -- the
      // API takes one, and activation only stamps the session's date onto
      // participants who have none -- but nothing in the UI sets one, so the
      // two columns showed the same value on every row. This shows the date
      // that applies to the reader, and says so only when it is not the
      // session's.
      title: 'Due', key: 'Due', width: 130,
      render: (_, r) => {
        const mine = r.MyDueDate || r.DueDate;
        if (!mine) return <Typography.Text type="secondary">—</Typography.Text>;
        const differs = r.MyDueDate && r.DueDate && r.MyDueDate !== r.DueDate;
        return (
          <span>
            {mine}
            {differs && (
              <Tooltip title={`Your due date. The session is due ${r.DueDate}.`}>
                <Tag color="blue" style={{ marginLeft: 6 }}>yours</Tag>
              </Tooltip>
            )}
          </span>
        );
      },
    },
    { title: 'Created On',  dataIndex: 'CreatedAt',  key: 'CreatedAt',  width: 175, render: (v) => formatTS(v) },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>My Coaching Sessions as a Participant</Typography.Title>
        <Space size={4}>
          <span style={{ fontSize: 13, whiteSpace: 'nowrap' }}>My Status</span>
          <Select
            value={partStatus}
            onChange={setPartStatus}
            options={PART_STATUS_OPTIONS}
            style={{ width: 150 }}
          />
        </Space>
      </div>

      <Table
        rowKey="ID"
        dataSource={filtered}
        columns={columns}
        loading={loading}
        size="small"
        pagination={{ defaultPageSize: 25, showTotal: (t) => `${t} sessions` }}
        locale={{ emptyText: 'No coaching sessions found.' }}
        onRow={(r) => ({ onClick: () => navigate(`/coaching/sessions/${r.ID}`), style: { cursor: 'pointer' } })}
      />
    </>
  );
}
